import {endOfDay} from '../../utils/date-range-format.service';
import {Injectable, isDevMode, OnDestroy} from '@angular/core';
import {AssessmentModel} from "../../interfaces/AssessmentModel";
import {Subject} from "rxjs";
import {AngularFirestore} from "@angular/fire/compat/firestore";
import {shareReplay} from "rxjs/operators";
import {environment} from "../../../environments/environment";
import {ɵFormGroupValue, ɵTypedOrUntyped} from "@angular/forms";
import {AngularFireDatabase} from "@angular/fire/compat/database";
import firebase from "firebase/compat/app";
import {collection, getCountFromServer, getFirestore, query, where} from "firebase/firestore";

@Injectable({
  providedIn: 'root'
})
export class CounselingService implements OnDestroy {
  private _destroyed$ = new Subject()
  env = isDevMode() ? environment.testMode : environment.productionMode

  private centreDataSource = new Subject();
  public data = this.centreDataSource.asObservable();

  // ========================================================
  // 📊 COUNSELLING MONTHLY SUMMARY — pre-aggregated counters
  // ========================================================
  private readonly summaryCollection = 'counsellingMonthlySummary';
  private readonly FIRESTORE_IN_LIMIT = 10;

  constructor(private readonly mFirestore: AngularFirestore,
              private readonly mDatabase: AngularFireDatabase,
  ) {
  }

  // ─── Summary helper methods ───

  private toJsDate(d: any): Date {
    if (!d) return new Date();
    if (d?.toDate) return d.toDate();
    if (d instanceof Date) return d;
    if (d?.seconds) return new Date(d.seconds * 1000);
    return new Date(d);
  }

  private getYearMonth(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  }

  private sanitizeForDocId(name: string): string {
    return name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  }

  private summaryDocId(counsellorName: string, yearMonth: string, centreCode: string): string {
    return `${this.sanitizeForDocId(counsellorName)}_${yearMonth}_${centreCode}`;
  }

  private getYearMonthsInRange(start: Date, end: Date): string[] {
    const months: string[] = [];
    let cur = new Date(start.getFullYear(), start.getMonth(), 1);
    const endMo = new Date(end.getFullYear(), end.getMonth(), 1);
    while (cur <= endMo) {
      months.push(this.getYearMonth(cur));
      cur.setMonth(cur.getMonth() + 1);
    }
    return months;
  }

  private chunkArray<T>(arr: T[], maxSize: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < arr.length; i += maxSize) {
      chunks.push(arr.slice(i, i + maxSize));
    }
    return chunks;
  }

  /**
   * Update pre-aggregated counselling summary counter.
   * fields.individual, fields.group, fields.followUp, fields.observed: optional increments.
   */
  updateSummaryCounter(
    counsellorName: string,
    centreCode: string,
    sessionDate: any,
    fields: { individual?: number; group?: number; followUp?: number; observed?: number }
  ): Promise<void> {
    if (!counsellorName || !centreCode || !sessionDate) return Promise.resolve();
    const date = this.toJsDate(sessionDate);
    const yearMonth = this.getYearMonth(date);
    const docId = this.summaryDocId(counsellorName, yearMonth, centreCode);

    const update: any = {
      counsellorName,
      centreCode,
      yearMonth,
      year: date.getFullYear(),
      month: date.getMonth() + 1,
    };
    if (fields.individual !== undefined && fields.individual !== 0) {
      update['individualCount'] = firebase.firestore.FieldValue.increment(fields.individual);
    }
    if (fields.group !== undefined && fields.group !== 0) {
      update['groupCount'] = firebase.firestore.FieldValue.increment(fields.group);
    }
    if (fields.followUp !== undefined && fields.followUp !== 0) {
      update['followUpCount'] = firebase.firestore.FieldValue.increment(fields.followUp);
    }
    if (fields.observed !== undefined && fields.observed !== 0) {
      update['observedCount'] = firebase.firestore.FieldValue.increment(fields.observed);
    }

    return this.mFirestore.firestore
      .collection(this.summaryCollection)
      .doc(docId)
      .set(update, { merge: true });
  }

  /**
   * Query pre-aggregated counselling summary for a date range + optional centre(s).
   * When dates are null, fetches ALL summary docs (no date filter).
   * Properly chunks yearMonth 'in' queries to stay within Firestore's limit of 10.
   */
  getSummaryForDateRange(
    centreCode: string | string[],
    startDate: Date | null | undefined,
    endDate: Date | null | undefined
  ): Promise<any[]> {
    const codes: string[] = Array.isArray(centreCode)
      ? centreCode.filter(c => !!c && c !== 'All')
      : (centreCode && centreCode !== 'All' ? [centreCode] : []);

    // ── No date filter → fetch ALL summary docs (optionally filtered by centre) ──
    if (!startDate || !endDate) {
      if (codes.length === 0) {
        return this.mFirestore.firestore.collection(this.summaryCollection).get()
          .then(snap => snap.docs.map(d => d.data()));
      }
      if (codes.length === 1) {
        return this.mFirestore.firestore.collection(this.summaryCollection)
          .where('centreCode', '==', codes[0]).get()
          .then(snap => snap.docs.map(d => d.data()));
      }
      // Multiple centres — chunk centre codes
      const centreChunks = this.chunkArray(codes, this.FIRESTORE_IN_LIMIT);
      return Promise.all(centreChunks.map(chunk =>
        this.mFirestore.firestore.collection(this.summaryCollection)
          .where('centreCode', 'in', chunk).get()
          .then(snap => snap.docs.map(d => d.data()))
      )).then(results => results.flat());
    }

    // ── Date range provided → chunk yearMonths into batches of 10 ──
    const yearMonths = this.getYearMonthsInRange(startDate, endDate);
    if (yearMonths.length === 0) return Promise.resolve([]);

    const ymChunks = this.chunkArray(yearMonths, this.FIRESTORE_IN_LIMIT);

    if (codes.length <= 1) {
      // Single centre or all centres — one query per yearMonth chunk
      return Promise.all(ymChunks.map(ymBatch => {
        let query: firebase.firestore.Query = this.mFirestore.firestore.collection(this.summaryCollection);
        query = query.where('yearMonth', 'in', ymBatch);
        if (codes.length === 1) query = query.where('centreCode', '==', codes[0]);
        return query.get().then(snap => snap.docs.map(d => d.data()));
      })).then(results => results.flat());
    }

    // Multiple centres + multiple yearMonth chunks — per-centre per-chunk queries
    const promises: Promise<any[]>[] = [];
    for (const ymBatch of ymChunks) {
      for (const code of codes) {
        let q: firebase.firestore.Query = this.mFirestore.firestore.collection(this.summaryCollection);
        q = q.where('yearMonth', 'in', ymBatch).where('centreCode', '==', code);
        promises.push(q.get().then(snap => snap.docs.map(d => d.data())));
      }
    }
    return Promise.all(promises).then(results => results.flat());
  }

  /**
   * Get ALL summary docs (no date filter). Used when no date range is selected.
   */
  getAllSummary(): Promise<any[]> {
    return this.mFirestore.firestore
      .collection(this.summaryCollection)
      .get()
      .then(snap => snap.docs.map(d => d.data()));
  }

  getAssessMentsList() {
    let userData = JSON.parse(localStorage.getItem('userData')!)
    console.log('into getallcentres')
    let query = this.mFirestore.collection<AssessmentModel>(this.env.assessment + '/allAssessments/assessments/', ref => ref.orderBy('dateOfAssessment', 'desc')).valueChanges({idField: 'id'})
    if (userData.role == 'Centre' || userData.role == 'Admin' || userData.role == 'Psychologist') query = this.getSortedCounsellingListByRole(userData.centreCode)
    return query
  }

  /**
   * Fetch assessments filtered by date range.
   * Returns a Promise (one-shot read) — no real-time listener overhead.
   * When start/end are null, fetches all-time assessments.
   */
  getAssessmentsListByDateRange(start: Date | null, end: Date | null): Promise<any[]> {
    const path = this.env.assessment + '/allAssessments/assessments/';
    if (start && end) {
      return this.mFirestore.firestore
        .collection(path)
        .where('dateOfAssessment', '>=', start)
        .where('dateOfAssessment', '<=', endOfDay(end))
        .get()
        .then(snap => snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }
    return this.mFirestore.firestore
      .collection(path)
      .get()
      .then(snap => snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }

  getUpcomingAssessMentsList(centreCode: string, previousDate: Date, threshold: Date) {
    let userData = JSON.parse(localStorage.getItem('userData')!)
    console.log('into getallcentres')
    let query = this.mFirestore.collection<AssessmentModel>(this.env.assessment + '/allAssessments/assessments/', ref => ref.where('centreCode', '==', centreCode).where("dateOfAssessment", '>=', previousDate).where("dateOfAssessment", '<=', threshold)).valueChanges({idField: 'id'})
    if(centreCode!='All') query = this.mFirestore.collection<AssessmentModel>(this.env.assessment + '/allAssessments/assessments/', ref => ref.where("dateOfAssessment", '>=', previousDate).where("dateOfAssessment", '<=', threshold)).valueChanges({idField: 'id'})

    // if(userData.role=='Centre'  ||userData.role =='Admin' || userData.role =='Psychologist') query =   this.getSortedCounsellingListByRole(userData.centreCode)
    return query


  }

  getAssessMentsListSorted(centreCode: string) {
    console.log('in counsellin service: ' + centreCode)
    let userData = JSON.parse(localStorage.getItem('userData')!)
    console.log('into getallcentres')
    console.log('ROLE=>', userData.role)
    let query = this.mFirestore.collection<AssessmentModel>(this.env.assessment + '/allAssessments/assessments/').valueChanges({idField: 'id'})

    if (centreCode == 'All') query = this.mFirestore.collection<AssessmentModel>(this.env.assessment + '/allAssessments/assessments/', ref => ref.orderBy('dateOfAssessment', 'desc')).valueChanges({idField: 'id'})
    else query = this.mFirestore.collection<AssessmentModel>(this.env.assessment + '/allAssessments/assessments/', ref => ref.orderBy('dateOfAssessment', 'desc').where('centreCode', '==', centreCode)).valueChanges({idField: 'id'})


    return query


  }

  getSortedCounsellingListByRole(centreCode: string) {
    const centerRef = this.mFirestore.collection<AssessmentModel>(this.env.counseling, ref1 => ref1.orderBy('dateOfAssessment', 'desc').where('centreCode', '==', centreCode)
      // .orderBy("sr", 'desc')
    )

    return centerRef.valueChanges({idField: 'id'}).pipe(
      // tap(arr=>console.log(`centre: read (${ this.centreRead +=arr.length } )docs` ),
      shareReplay(1)
    )
  }

  ngOnDestroy(): void {
    // @ts-ignore
    this._destroyed$.next()
    this._destroyed$.complete()
  }

  addAssessment(form: any) {
    const childCode = form['childCode']
    const type = form['type']
    console.log(childCode)
    console.log(type)
    const batchWrite = this.mFirestore.firestore.batch()
    //* We used Realtime Database only to get pushId in below line
    const dockey = this.mDatabase.database.ref(`${this.env.assessment}/childAssessment/${childCode}`).push().key
    const childSessionRef = this.mFirestore.firestore.doc(`${this.env.assessment}/childAssessment/${childCode}/${dockey}`)
    const allSessionRef = this.mFirestore.firestore.doc(`${this.env.assessment}/allAssessments/assessments/${dockey}`)
    batchWrite.set(childSessionRef, form, {merge: true})
    batchWrite.set(allSessionRef, form, {merge: true})

    return batchWrite.commit()
    // console.assessment-table(form)
  }

  addIndividualSession(form: ɵTypedOrUntyped<any, ɵFormGroupValue<any>, any>) {
    const batchWrite = this.mFirestore.firestore.batch()
    const childCode = form['childCode']
    //* We used Realtime Database only to get pushId in below line
    const dockey = this.mDatabase.database.ref(`${this.env.counseling}/childSession/${childCode}`).push().key
    const childSessionRef = this.mFirestore.firestore.doc(`${this.env.counseling}/childSessions/${childCode}/${dockey}`)
    const allSessionRef = this.mFirestore.firestore.doc(`${this.env.counseling}/allSessions/sessions/${dockey}`)
    batchWrite.set(childSessionRef, form, {merge: true})
    batchWrite.set(allSessionRef, form, {merge: true})

    // ✅ Maintain counselling summary counter
    if (form['submittedBy'] && form['centreCode'] && form['date']) {
      this.updateSummaryCounter(form['submittedBy'], form['centreCode'], form['date'], { individual: 1 })
        .catch(e => console.warn('⚠️ Counselling summary counter update failed:', e));
    }

    return batchWrite.commit()
  }

  addGroupSession(form: ɵTypedOrUntyped<any, ɵFormGroupValue<any>, any>) {
    const batchWrite = this.mFirestore.firestore.batch()
    const dockey = this.mDatabase.database.ref(`${this.env.counseling}/childSession/`).push().key
    const allSessionRef = this.mFirestore.firestore.doc(`${this.env.counseling}/allSessions/groupSessions/${dockey}`)
    batchWrite.set(allSessionRef, form, {merge: true})

    // ✅ Maintain counselling summary counter
    if (form['submittedBy'] && form['date']) {
      const centreCode = form['centreCode'] || 'All';
      this.updateSummaryCounter(form['submittedBy'], centreCode, form['date'], { group: 1 })
        .catch(e => console.warn('⚠️ Counselling summary counter update failed:', e));
    }

    return batchWrite.commit()
  }
  fetchGroupSessions() {
    return this.mFirestore.collection<any>(this.env.counseling + '/allSessions/groupSessions', ref => ref.orderBy('date', 'desc')).valueChanges({idField: 'id'})
  }
  fetchIndividualSession() {
    return this.mFirestore.collection<any>(this.env.counseling + '/allSessions/sessions', ref => ref.orderBy('date', 'desc')).valueChanges({idField: 'id'})
  }
  fetchFollowUpSession(){
    return this.mFirestore.collection<any>(this.env.counsellingFollowUp, ref => ref.orderBy('date', 'desc')).valueChanges({idField: 'id'})
  }
  fetchGroupSessionsfrom2024(date:Date) {
    return this.mFirestore.collection<any>(this.env.counseling + '/allSessions/groupSessions', ref => ref.orderBy('date', 'desc')
      .where('date','>=',date)).valueChanges({idField: 'id'})
  }
  fetchGroupSessionsfrom2024Borivali(date:Date) {
    return this.mFirestore.collection<any>(this.env.counseling + '/allSessions/groupSessions', ref => ref.orderBy('date', 'desc')
      .where('centreCode','==','ALBorivali')
      .where('date','>=',date)).valueChanges({idField: 'id'})
  }
  fetchGroupSessionsByCentre(centrecode: string, centreName: string) {
    let obj = {
      centreName: centreName,
      centreCode: centrecode
    }
    return this.mFirestore.collection(this.env.counseling + '/allSessions/groupSessions', ref => ref.where('centres', 'array-contains', obj).orderBy('date', 'desc')).valueChanges({idField: 'id'})
  }

  fetchIndividualSessionsByCentreByDate(centrecode: string, startDate: Date, endDate: Date) {
    return this.mFirestore.collection(this.env.counseling + '/allSessions/sessions', ref => ref.where('centreCode', '==', centrecode)
      .where('date', '>=', startDate)
      .where('date', '<=', endDate)
      .orderBy('date', 'desc')).valueChanges({idField: 'id'})
  }

  fetchGroupSessionsByCentreByDate(centrecode: string, startDate: Date, endDate: Date) {
    if (centrecode != 'All') {
      return this.mFirestore.collection(this.env.counseling + '/allSessions/groupSessions', ref => ref.where('centreCode', '==', centrecode)
        .where('date', '>=', startDate)
        .where('date', '<=', endDate)
        .orderBy('date', 'desc')).valueChanges({idField: 'id'})
    } else {
      return this.mFirestore.collection(this.env.counseling + '/allSessions/groupSessions', ref => ref
        .where('date', '>=', startDate)
        .where('date', '<=', endDate)
        .orderBy('date', 'desc')).valueChanges({idField: 'id'})
    }
  }


  fetchGroupSessionsByChild(childObject: any) {
    return this.mFirestore.collection(this.env.counseling + '/allSessions/groupSessions', ref => ref.orderBy('date', 'desc').where('children', 'array-contains', childObject)).valueChanges({idField: 'id'})
  }

  fetchGroupSessionsSorted(selectedCentre: any) {
    if (selectedCentre.centreCode == 'All') return this.mFirestore.collection(this.env.counseling + '/allSessions/groupSessions', ref => ref.orderBy('date', 'desc')).valueChanges({idField: 'id'})

    return this.mFirestore.collection(this.env.counseling + '/allSessions/groupSessions', ref => ref.orderBy('date', 'desc').where('centreCode', '==', selectedCentre.centreCode)).valueChanges({idField: 'id'})

  }


  fetchIndividualSessions() {
    return this.mFirestore.collection(this.env.counseling + '/allSessions/sessions').valueChanges({idField: 'id'})

  }
  fetchIndividualSessionsRegular() {
    return this.mFirestore.collection(this.env.counseling + '/allSessions/sessions',ref => ref.where(
      'type','==','Regular Counselling'
    )).valueChanges({idField: 'id'})

  }
  fetchIndividualSessionsRegularFrom2024(date:Date) {
    return this.mFirestore.collection(this.env.counseling + '/allSessions/sessions',ref => ref.where
      ('type', 'in', ['Regular Counselling', 'Follow-up Counselling'])
      .where('date','>=',date)
    ).valueChanges({idField: 'id'})

  }
  fetchIndividualSessionsRegularFrom2024Borivali(date:Date) {
    return this.mFirestore.collection(this.env.counseling + '/allSessions/sessions',ref =>
      ref.where('centreCode','==','ALBorivali')
      .where
      ('type', 'in', ['Regular Counselling', 'Follow-up Counselling'])
        .where('date','>=',date)
    ).valueChanges({idField: 'id'})

  }


  fetchAssessmentsFrom2024(date:Date) {
    return this.mFirestore.collection( 'Assessments/allAssessments/assessments',ref => ref.where
      ('type', 'in', ['Admission Baseline Assessment', 'Intermediate Assessment','Discharge Assessment'])
        .where('dateOfAssessment','>=',date)
    ).valueChanges({idField: 'id'})

  }
  fetchAssessmentsFrom2024Borivali(date:Date) {
    return this.mFirestore.collection( 'Assessments/allAssessments/assessments',ref => ref.where
      ('type', 'in', ['Admission Baseline Assessment', 'Intermediate Assessment','Discharge Assessment'])
        .where('dateOfAssessment','>=',date)
      .where('centreCode','>=','ALBorivali')
    ).valueChanges({idField: 'id'})

  }


  fetchfollowupAssessmentsFrom2024(date:Date) {
    return this.mFirestore.collection(this.env.counsellingFollowUp,ref => ref
      .where('date','>=',date)
    ).valueChanges({idField: 'id'})

  }
  fetchfollowupAssessmentsFrom2024Borivali(date:Date) {
    return this.mFirestore.collection(this.env.counsellingFollowUp,ref => ref
      .where('date','>=',date)
      .where('centreCode','>=','ALBorivali')

    ).valueChanges({idField: 'id'})

  }
  //* Count-only variants using Firestore count() aggregation — avoids downloading full documents just to read .length
  fetchIndividualSessionsRegularFrom2024Count(date: Date): Promise<number> {
    const q = query(
      collection(getFirestore(), this.env.counseling + '/allSessions/sessions'),
      where('type', 'in', ['Regular Counselling', 'Follow-up Counselling']),
      where('date', '>=', date)
    )
    return getCountFromServer(q).then(snap => snap.data().count)
  }

  fetchGroupSessionsfrom2024Count(date: Date): Promise<number> {
    const q = query(
      collection(getFirestore(), this.env.counseling + '/allSessions/groupSessions'),
      where('date', '>=', date)
    )
    return getCountFromServer(q).then(snap => snap.data().count)
  }

  fetchAssessmentsFrom2024Count(date: Date): Promise<number> {
    const q = query(
      collection(getFirestore(), 'Assessments/allAssessments/assessments'),
      where('type', 'in', ['Admission Baseline Assessment', 'Intermediate Assessment', 'Discharge Assessment']),
      where('dateOfAssessment', '>=', date)
    )
    return getCountFromServer(q).then(snap => snap.data().count)
  }

  fetchfollowupAssessmentsFrom2024Count(date: Date): Promise<number> {
    const q = query(
      collection(getFirestore(), this.env.counsellingFollowUp),
      where('date', '>=', date)
    )
    return getCountFromServer(q).then(snap => snap.data().count)
  }

  fetchIndividualSessionsRegularFrom2024BorivaliCount(date: Date): Promise<number> {
    const q = query(
      collection(getFirestore(), this.env.counseling + '/allSessions/sessions'),
      where('centreCode', '==', 'ALBorivali'),
      where('type', 'in', ['Regular Counselling', 'Follow-up Counselling']),
      where('date', '>=', date)
    )
    return getCountFromServer(q).then(snap => snap.data().count)
  }

  fetchGroupSessionsfrom2024BorivaliCount(date: Date): Promise<number> {
    const q = query(
      collection(getFirestore(), this.env.counseling + '/allSessions/groupSessions'),
      where('centreCode', '==', 'ALBorivali'),
      where('date', '>=', date)
    )
    return getCountFromServer(q).then(snap => snap.data().count)
  }

  fetchAssessmentsFrom2024BorivaliCount(date: Date): Promise<number> {
    const q = query(
      collection(getFirestore(), 'Assessments/allAssessments/assessments'),
      where('type', 'in', ['Admission Baseline Assessment', 'Intermediate Assessment', 'Discharge Assessment']),
      where('dateOfAssessment', '>=', date),
      where('centreCode', '>=', 'ALBorivali')
    )
    return getCountFromServer(q).then(snap => snap.data().count)
  }

  fetchfollowupAssessmentsFrom2024BorivaliCount(date: Date): Promise<number> {
    const q = query(
      collection(getFirestore(), this.env.counsellingFollowUp),
      where('date', '>=', date),
      where('centreCode', '>=', 'ALBorivali')
    )
    return getCountFromServer(q).then(snap => snap.data().count)
  }

  // ── Centre + date-range counts, for the MIS dashboard cards ─────────────────
  // The pre-existing *From2024Count / *ByCentreCount pairs are either date-bounded or
  // centre-bounded, never both, which is why those cards could not follow the filters.
  // Each of these needs a composite index; Firestore's console error links create them.

  /** Regular + follow-up individual sessions for one centre over a date range. */
  countIndividualSessions(centreCode: string | null, startDate: Date | null, endDate: Date | null): Promise<number> {
    const filters: any[] = [where('type', 'in', ['Regular Counselling', 'Follow-up Counselling'])];
    if (centreCode && centreCode !== 'All') filters.push(where('centreCode', '==', centreCode));
    if (startDate) filters.push(where('date', '>=', startDate));
    if (endDate) filters.push(where('date', '<=', endDate));
    const q = query(collection(getFirestore(), this.env.counseling + '/allSessions/sessions'), ...filters);
    return getCountFromServer(q).then(snap => snap.data().count);
  }

  /**
   * Group sessions for one centre over a date range.
   * Matches the existing behaviour: the centre is held in a `centres` array of
   * {centreCode, centreName} objects, so this is an object equality inside an array —
   * it stops matching if a centre is ever renamed.
   */
  countGroupSessions(centreCode: string | null, centreName: string, startDate: Date | null, endDate: Date | null): Promise<number> {
    const filters: any[] = [];
    if (centreCode && centreCode !== 'All') {
      filters.push(where('centres', 'array-contains', {centreCode, centreName}));
    }
    if (startDate) filters.push(where('date', '>=', startDate));
    if (endDate) filters.push(where('date', '<=', endDate));
    const q = query(collection(getFirestore(), this.env.counseling + '/allSessions/groupSessions'), ...filters);
    return getCountFromServer(q).then(snap => snap.data().count);
  }

  /**
   * Assessments for one centre over a date range.
   * Uses env.assessment rather than the hardcoded 'Assessments/...' path the older
   * count methods use, so dev mode reads dev data. Identical in production.
   */
  countAssessments(centreCode: string | null, startDate: Date | null, endDate: Date | null): Promise<number> {
    const filters: any[] = [where('type', 'in', ['Admission Baseline Assessment', 'Intermediate Assessment', 'Discharge Assessment'])];
    if (centreCode && centreCode !== 'All') filters.push(where('centreCode', '==', centreCode));
    if (startDate) filters.push(where('dateOfAssessment', '>=', startDate));
    if (endDate) filters.push(where('dateOfAssessment', '<=', endDate));
    const q = query(collection(getFirestore(), this.env.assessment + '/allAssessments/assessments'), ...filters);
    return getCountFromServer(q).then(snap => snap.data().count);
  }

  /** Follow-up assessments for one centre over a date range. */
  countFollowUpAssessments(centreCode: string | null, startDate: Date | null, endDate: Date | null): Promise<number> {
    const filters: any[] = [];
    if (centreCode && centreCode !== 'All') filters.push(where('centreCode', '==', centreCode));
    if (startDate) filters.push(where('date', '>=', startDate));
    if (endDate) filters.push(where('date', '<=', endDate));
    const q = query(collection(getFirestore(), this.env.counsellingFollowUp), ...filters);
    return getCountFromServer(q).then(snap => snap.data().count);
  }

  fetchIndividualSessionsByCentreCount(centreCode: any): Promise<number> {
    const q = query(
      collection(getFirestore(), this.env.counseling + '/allSessions/sessions'),
      where('centreCode', '==', centreCode)
    )
    return getCountFromServer(q).then(snap => snap.data().count)
  }

  fetchGroupSessionsByCentreCount(centrecode: string, centreName: string): Promise<number> {
    const obj = {centreName: centreName, centreCode: centrecode}
    const q = query(
      collection(getFirestore(), this.env.counseling + '/allSessions/groupSessions'),
      where('centres', 'array-contains', obj)
    )
    return getCountFromServer(q).then(snap => snap.data().count)
  }

  fetchIndividualSessions2024(date:Date) {
    return this.mFirestore.collection(this.env.counseling + '/allSessions/sessions',ref =>
      ref.where('sessionDate','>=',date
    )).valueChanges({idField: 'id'})

  }
  fetchIndividualSessionsByCentre(centreCode: any) {
    return this.mFirestore.collection(this.env.counseling + '/allSessions/sessions', ref => ref.where('centreCode', '==', centreCode)).valueChanges({idField: 'id'})

  }

  fetchIndividualSessionsByChild(selectedChildCode: string) {
    return this.mFirestore.collection(this.env.counseling + '/allSessions/sessions', ref => ref.
        orderBy('date','desc')
        .where('childCode', '==', selectedChildCode)).valueChanges({idField: 'id'})

  }

  fetchIndividualSessionsWithSorting(centreCode: string) {
    if (centreCode == 'All') return this.mFirestore.collection(this.env.counseling + '/allSessions/sessions', ref => ref.orderBy('date', 'desc')).valueChanges({idField: 'id'})
    else return this.mFirestore.collection(this.env.counseling + '/allSessions/sessions', ref => ref.orderBy('date', 'desc').where('centreCode', '==', centreCode)).valueChanges({idField: 'id'})

  }

  UploadTestCounselling(centreCode: string) {
    if (centreCode == 'All') return this.mFirestore.collection(this.env.counseling + '/allSessions/sessions').valueChanges({idField: 'id'})
    else return this.mFirestore.collection(this.env.counsellingUploadTest, ref => ref.where('centreCode', '==', centreCode)).valueChanges({idField: 'id'})

  }

  editIndividualSession(form: ɵTypedOrUntyped<any, ɵFormGroupValue<any>, any>, id: string) {
    const batchWrite = this.mFirestore.firestore.batch()
    const childCode = form['childCode']
    //* We used Realtime Database only to get pushId in below line
    const dockey = id
    const childSessionRef = this.mFirestore.firestore.doc(`${this.env.counseling}/childSessions/${childCode}/${dockey}`)
    const allSessionRef = this.mFirestore.firestore.doc(`${this.env.counseling}/allSessions/sessions/${dockey}`)
    batchWrite.update(childSessionRef, form, {merge: true})
    batchWrite.update(allSessionRef, form, {merge: true})
    return batchWrite.commit()
  }

  editGroupSession(form: ɵTypedOrUntyped<any, ɵFormGroupValue<any>, any>, id: string) {
    const batchWrite = this.mFirestore.firestore.batch()
    // const childCode = form['childCode']
    //* We used Realtime Database only to get pushId in below line
    const dockey = id
    // const childSessionRef = this.mFirestore.firestore.doc(`${this.env.counseling}/childSessions/${childCode}/${dockey}`)
    const allSessionRef = this.mFirestore.firestore.doc(`${this.env.counseling}/allSessions/groupSessions/${dockey}`)
    // batchWrite.set(childSessionRef,form,{merge:true})
    batchWrite.update(allSessionRef, form, {merge: true})
    return batchWrite.commit()
  }

  editAssessment(form: any, id: string) {
    const childCode = form['childCode']
    const type = form['type']
    console.log(childCode)
    console.log(type)
    const batchWrite = this.mFirestore.firestore.batch()
    //* We used Realtime Database only to get pushId in below line
    const dockey = id
    const childSessionRef = this.mFirestore.firestore.doc(`${this.env.assessment}/childAssessment/${childCode}/${dockey}`)
    const allSessionRef = this.mFirestore.firestore.doc(`${this.env.assessment}/allAssessments/assessments/${dockey}`)
    batchWrite.update(childSessionRef, form, {merge: true})
    batchWrite.update(allSessionRef, form, {merge: true})

    return batchWrite.commit()
  }

  deleteIndividualSession(element: any) {
    // ✅ Decrement counselling summary counter
    if (element.submittedBy && element.centreCode && element.date) {
      this.updateSummaryCounter(element.submittedBy, element.centreCode, element.date, { individual: -1 })
        .catch(e => console.warn('⚠️ Counselling summary counter update failed:', e));
    }

    const batchWrite = this.mFirestore.firestore.batch()
    const childCode = element.childCode
    const dockey = element.id
    const childSessionRef = this.mFirestore.firestore.doc(`${this.env.counseling}/childSessions/${childCode}/${dockey}`)
    const allSessionRef = this.mFirestore.firestore.doc(`${this.env.counseling}/allSessions/sessions/${dockey}`)
    batchWrite.delete(childSessionRef)
    batchWrite.delete(allSessionRef)
    return batchWrite.commit()
  }

  deleteGroupAssessment(element: any) {
    // ✅ Decrement counselling summary counter
    if (element.submittedBy && element.date) {
      const centreCode = element.centreCode || 'All';
      this.updateSummaryCounter(element.submittedBy, centreCode, element.date, { group: -1 })
        .catch(e => console.warn('⚠️ Counselling summary counter update failed:', e));
    }

    const batchWrite = this.mFirestore.firestore.batch()
    const dockey = element.id
    const allSessionRef = this.mFirestore.firestore.doc(`${this.env.counseling}/allSessions/groupSessions/${dockey}`)
    batchWrite.delete(allSessionRef)
    return batchWrite.commit()
  }

  deleteAssessment(element: any) {
    const childCode = element['childCode']
    const type = element['type']
    console.log(childCode)
    console.log(type)
    const batchWrite = this.mFirestore.firestore.batch()
    //* We used Realtime Database only to get pushId in below line
    const dockey = element.id
    const childSessionRef = this.mFirestore.firestore.doc(`${this.env.assessment}/childAssessment/${childCode}/${dockey}`)
    const allSessionRef = this.mFirestore.firestore.doc(`${this.env.assessment}/allAssessments/assessments/${dockey}`)
    batchWrite.delete(childSessionRef)
    batchWrite.delete(allSessionRef)

    return batchWrite.commit()
  }

  fectchAssessmentsByChild(selectedChildCode: string) {
    return this.mFirestore.collection<AssessmentModel>('Assessments/allAssessments/assessments/', ref => ref.where('childCode', '==', selectedChildCode)).valueChanges({idField: 'id'})
  }
  CounselingFollowUpAssessmentByChild(selectedChildCode: string) {
    return this.mFirestore.collection<AssessmentModel>(this.env.counsellingFollowUp, ref => ref
        .orderBy('date','desc')
        .where('childCode', '==', selectedChildCode)).valueChanges({idField: 'id'})
  }
  updateCentreName(value: any) {
    this.centreDataSource.next(value);
  }

  fetchIndividualSessionsWithSortingByDate(centreCode: string, start: Date | undefined, end: Date) {
    if (centreCode == 'All') return this.mFirestore.collection(this.env.counseling + '/allSessions/sessions', ref => ref.orderBy('date', 'desc').where('date', '>=', start).where('date', '<=', end)).valueChanges({idField: 'id'})
    else return this.mFirestore.collection(this.env.counseling + '/allSessions/sessions', ref => ref.orderBy('date', 'desc').where('centreCode', '==', centreCode).where('date', '>=', start).where('date', '<=', end)).valueChanges({idField: 'id'})

  }

  /**
   * Toggle the highlight field for an individual counseling session.
   * Updates only the all-sessions document.
   * @param element - The session element containing id
   * @returns Promise from the update operation
   */
  toggleIndividualSessionHighlight(element: any) {
    const dockey = element.id;
    const newHighlightValue = !element.isHighlight; // Toggle the current value
    const allSessionRef = this.mFirestore.firestore.doc(`${this.env.counseling}/allSessions/sessions/${dockey}`);

    return allSessionRef.update({ isHighlight: newHighlightValue });
  }
}
